import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ExternalLink,
  KeyRound,
  Loader2,
  Search,
  Webhook,
  Wrench,
  X,
} from "lucide-react";
import { Aurora } from "@/components/motion/aurora";
import { FadeIn } from "@/components/motion/fade-in";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IntegrationLogo } from "@/components/integrations/logo";
import { integrationsApi } from "@/lib/integrations-api";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "@/data/integrations";
import { cn } from "@/lib/utils";

/**
 * Friendly category label. Looks up the curated label in CATEGORY_LABELS;
 * falls back to a sentence-cased version of the raw slug. So `ai-llm`
 * renders as "AI & LLMs", `issue-tracking` as "Issue Tracking", and
 * a brand-new uncatalogued category like `payments` becomes "Payments".
 */
function prettyCategory(c: string): string {
  if (!c) return c;
  return (
    CATEGORY_LABELS[c] ??
    c
      .split("-")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ")
  );
}

/**
 * Sort integrations into curated order:
 *   1. Categories follow CATEGORY_ORDER (the rank-aware order from
 *      data/integrations.ts; uncatalogued categories go to the end).
 *   2. Within a category, preserve the server's REGISTRY order (the
 *      top100 rank). NO alphabetical sort — top integrations win.
 */
function sortByCuratedOrder<T extends { category: string }>(
  rows: T[],
): T[] {
  const rank = new Map<string, number>();
  CATEGORY_ORDER.forEach((c, i) => rank.set(c, i));
  return [...rows]
    .map((row, originalIdx) => ({ row, originalIdx }))
    .sort((a, b) => {
      const ar = rank.get(a.row.category) ?? 999;
      const br = rank.get(b.row.category) ?? 999;
      if (ar !== br) return ar - br;
      return a.originalIdx - b.originalIdx;
    })
    .map(({ row }) => row);
}

export function IntegrationsPage() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get("category");
  const [active, setActive] = useState<string>(initialCategory ?? "all");
  const [query, setQuery] = useState("");

  // Hits the server's /v1/integrations catalog endpoint — the same one
  // the console reads from. The runtime registry is the single source
  // of truth; this page reflects exactly what we ship, no separate
  // marketing list to keep in sync.
  const catalog = useQuery({
    queryKey: ["integrations"],
    queryFn: integrationsApi.list,
    staleTime: 60_000,
  });

  const integrations = useMemo(
    () => (catalog.data ? sortByCuratedOrder(catalog.data) : []),
    [catalog.data],
  );

  // Categories follow the same curated order as the integration grid.
  const categories = useMemo(() => {
    const present = new Set(integrations.map((i) => i.category));
    const rank = new Map<string, number>();
    CATEGORY_ORDER.forEach((c, i) => rank.set(c, i));
    return [...present].sort(
      (a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999),
    );
  }, [integrations]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: integrations.length };
    for (const cat of categories) {
      c[cat] = integrations.filter((i) => i.category === cat).length;
    }
    return c;
  }, [integrations, categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return integrations.filter((i) => {
      if (active !== "all" && i.category !== active) return false;
      if (!q) return true;
      return (
        i.name.toLowerCase().includes(q) ||
        i.slug.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q)
      );
    });
  }, [integrations, active, query]);

  const setCategory = (c: string) => {
    setActive(c);
    if (c === "all") {
      params.delete("category");
    } else {
      params.set("category", c);
    }
    setParams(params, { replace: true });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: window.scrollY, behavior: "smooth" });
    }
  };

  return (
    <div>
      <Hero total={integrations.length} />
      <section className="border-b border-border/40 bg-background">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                integrations.length > 0
                  ? `Search ${integrations.length} integration${integrations.length === 1 ? "" : "s"}…`
                  : "Search integrations…"
              }
              className="h-11 pl-10 text-base sm:h-12"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-[240px_1fr] lg:gap-8 lg:px-8">
          <aside className="-mx-4 min-w-0 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto lg:pr-2">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Categories
            </p>
            <nav className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-x-visible lg:px-0 lg:pb-0">
              <CategoryButton
                label="All"
                count={counts.all ?? 0}
                isActive={active === "all"}
                onClick={() => setCategory("all")}
              />
              {categories.map((cat) => (
                <CategoryButton
                  key={cat}
                  label={prettyCategory(cat)}
                  count={counts[cat] ?? 0}
                  isActive={active === cat}
                  onClick={() => setCategory(cat)}
                />
              ))}
            </nav>
          </aside>

          <div className="min-w-0">
            <div className="mb-4 flex items-end justify-between gap-4 sm:mb-6">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold tracking-tight sm:text-2xl">
                  {active === "all" ? "All integrations" : prettyCategory(active)}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                  {filtered.length}{" "}
                  {filtered.length === 1 ? "integration" : "integrations"}
                  {query && ` matching "${query}"`}
                </p>
              </div>
            </div>

            {catalog.isPending ? (
              <div className="flex h-40 items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : catalog.error ? (
              <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-8 text-center text-sm text-destructive sm:p-12">
                {(catalog.error as Error).message ??
                  "Could not load integrations"}
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border/60 bg-card/50 p-8 text-center sm:p-12">
                <p className="text-sm text-muted-foreground">
                  {integrations.length === 0
                    ? "No integrations have shipped yet."
                    : "No integrations match — try a different query or category."}
                </p>
                {integrations.length > 0 && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setQuery("");
                      setActive("all");
                    }}
                  >
                    Reset filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5">
                {filtered.map((integration, idx) => (
                  <FadeIn
                    key={integration.slug}
                    delay={Math.min(idx * 0.01, 0.3)}
                    y={8}
                    duration={0.35}
                  >
                    <Link
                      to={`/integrations/${integration.slug}`}
                      className="group relative flex h-full flex-col gap-3 rounded-xl border border-border/60 bg-card p-3 transition-all hover:border-border hover:shadow-md sm:p-4"
                    >
                      <IntegrationLogo
                        slug={integration.slug}
                        src={integration.logo}
                        name={integration.name}
                        size={36}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium leading-tight sm:text-base">
                          {integration.name}
                        </p>
                        <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground sm:text-xs">
                          {integration.description}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className="mt-auto w-fit max-w-full truncate text-[10px] font-normal text-muted-foreground"
                      >
                        {prettyCategory(integration.category)}
                      </Badge>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function CategoryButton({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex shrink-0 items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition lg:w-full",
        isActive
          ? "bg-foreground/10 text-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <span className="truncate">{label}</span>
      <span
        className={cn(
          "shrink-0 rounded-md px-1.5 py-0.5 text-[10px]",
          isActive
            ? "bg-foreground/15 text-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

function Hero({ total }: { total: number }) {
  const label =
    total === 0 ? "Integrations" : `${total} integration${total === 1 ? "" : "s"}`;
  return (
    <section className="relative isolate overflow-hidden border-b border-border/40 py-12 sm:py-16 lg:py-20">
      <Aurora className="opacity-50" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn immediate className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            {label}
          </Badge>
          <h1 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Every tool your agents will ever need
          </h1>
          <p className="mt-3 text-sm text-muted-foreground sm:mt-4 sm:text-base lg:text-lg">
            OAuth-grade integrations for SaaS, databases, dev tools, AI
            providers, and more. Connect once, expose to every agent runtime.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

/**
 * Friendly label for an auth strategy kind. The kit's wire kind is
 * machine-y; this is what we show to humans on the marketing page.
 */
function authLabel(kind: string): string {
  switch (kind) {
    case "oauth2":
    case "oauth2_built_in":
      return "OAuth 2.0";
    case "oauth2_cc":
      return "Client credentials";
    case "api_key":
      return "API key";
    case "bearer_token":
      return "Bearer token";
    case "basic":
      return "Basic auth";
    case "session":
      return "Session";
    case "iam":
      return "IAM role";
    case "none":
      return "No auth";
    default:
      return kind;
  }
}

/**
 * Detail page for `/integrations/:slug`. Renders the full catalog row:
 * hero with logo + name + counts, then tabs/sections for authentication,
 * tools, events, and versions.
 */
export function IntegrationDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const integration = useQuery({
    queryKey: ["integration", slug],
    queryFn: () => integrationsApi.get(slug),
    enabled: !!slug,
  });
  const [toolQuery, setToolQuery] = useState("");
  const [eventQuery, setEventQuery] = useState("");

  if (integration.isPending) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-24 text-center text-muted-foreground sm:px-6 lg:px-8">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (integration.error || !integration.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold">Integration not found</h1>
        <p className="mt-3 text-muted-foreground">
          We couldn't find an integration called <code>{slug}</code>.
        </p>
        <Button asChild className="mt-6">
          <Link to="/integrations">Back to integrations</Link>
        </Button>
      </div>
    );
  }

  const row = integration.data;
  const tq = toolQuery.trim().toLowerCase();
  const eq = eventQuery.trim().toLowerCase();
  const filteredTools = tq
    ? row.tools.filter(
        (t) =>
          t.name.toLowerCase().includes(tq) ||
          t.summary.toLowerCase().includes(tq) ||
          (t.description ?? "").toLowerCase().includes(tq),
      )
    : row.tools;
  const filteredEvents = eq
    ? row.events.filter(
        (e) =>
          e.name.toLowerCase().includes(eq) ||
          e.summary.toLowerCase().includes(eq) ||
          (e.description ?? "").toLowerCase().includes(eq),
      )
    : row.events;

  return (
    <div className="min-w-0">
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-border/40 py-10 sm:py-14 lg:py-16">
        <Aurora className="opacity-40" />
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <FadeIn immediate>
            <Link
              to={`/integrations?category=${encodeURIComponent(row.category)}`}
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              ← {prettyCategory(row.category)}
            </Link>
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
              <div className="shrink-0 rounded-2xl border border-border/60 bg-card p-3 shadow-sm sm:p-4">
                <IntegrationLogo
                  slug={row.slug}
                  src={row.logo}
                  name={row.name}
                  size={56}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                    {row.name}
                  </h1>
                  <Badge variant="secondary">{prettyCategory(row.category)}</Badge>
                </div>
                <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
                  {row.description}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {row.homepage && (
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={row.homepage}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Homepage
                        <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  )}
                  {row.docsUrl && (
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={row.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        API docs
                        <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Stats strip */}
            <div className="mt-8 grid grid-cols-3 gap-2 rounded-xl border border-border/60 bg-card/60 p-1 sm:gap-3 sm:p-2">
              <StatCell icon={Wrench} label="Tools" value={row.tools.length} />
              <StatCell icon={Webhook} label="Events" value={row.events.length} />
              <StatCell
                icon={KeyRound}
                label="Auth methods"
                value={row.auth.length}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20">
        <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 sm:space-y-12 sm:px-6 sm:py-14 lg:px-8">
          {/* Authentication */}
          <DetailSection
            title="Authentication"
            count={row.auth.length}
            description="How an end user (or your backend) hands MemKernel an upstream credential. Wire up once per project; the runtime stores and refreshes."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              {row.auth.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No authentication strategies declared yet.
                </p>
              ) : (
                row.auth.map((a, i) => (
                  <div
                    key={`${a.kind}-${i}`}
                    className="rounded-xl border border-border/60 bg-card p-4"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">{authLabel(a.kind)}</p>
                      {"provider" in a && a.provider ? (
                        <Badge variant="outline" className="text-[10px]">
                          {a.provider}
                        </Badge>
                      ) : null}
                    </div>
                    {"scopes" in a &&
                    a.scopes &&
                    Array.isArray(a.scopes.default) &&
                    a.scopes.default.length > 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Default scopes:{" "}
                        <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
                          {a.scopes.default.join(" ")}
                        </code>
                      </p>
                    ) : null}
                    {"fields" in a && Array.isArray(a.fields) && a.fields.length > 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Fields: {a.fields.map((f) => f.name).join(", ")}
                      </p>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </DetailSection>

          {/* Tools */}
          <DetailSection
            title="Tools"
            count={row.tools.length}
            description="Every action MemKernel can run on your behalf. Each tool is callable as MCP, OpenAI Function, Anthropic Tool, LangChain Tool, or a REST endpoint."
            search={{
              value: toolQuery,
              onChange: setToolQuery,
              placeholder: `Search ${row.tools.length} tools…`,
            }}
          >
            {filteredTools.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No tools match this filter.
              </p>
            ) : (
              <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card">
                {filteredTools.slice(0, 200).map((t) => (
                  <li key={t.name} className="p-4 sm:p-5">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <p className="font-mono text-sm font-medium text-foreground">
                        {row.slug}.{t.name}
                      </p>
                      {(t.tags ?? []).map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-[10px]"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {t.summary}
                    </p>
                    {t.description && t.description !== t.summary ? (
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground/80">
                        {t.description}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {filteredTools.length > 200 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Showing 200 of {filteredTools.length} — use the search to narrow.
              </p>
            )}
          </DetailSection>

          {/* Events */}
          <DetailSection
            title="Events"
            count={row.events.length}
            description="Inbound webhook events your agent can subscribe to. MemKernel verifies signatures, retries, and delivers normalized payloads."
            search={
              row.events.length > 0
                ? {
                    value: eventQuery,
                    onChange: setEventQuery,
                    placeholder: `Search ${row.events.length} events…`,
                  }
                : undefined
            }
          >
            {row.events.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This integration doesn't declare inbound events yet.
              </p>
            ) : filteredEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No events match this filter.
              </p>
            ) : (
              <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card">
                {filteredEvents.map((e) => (
                  <li key={e.name} className="p-4 sm:p-5">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <p className="font-mono text-sm font-medium text-foreground">
                        {row.slug}.{e.name}
                      </p>
                      {(e.tags ?? []).map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-[10px]"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {e.summary}
                    </p>
                    {e.description && e.description !== e.summary ? (
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground/80">
                        {e.description}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          {/* Versions */}
          {row.versions.length > 1 && (
            <DetailSection
              title="Versions"
              count={row.versions.length}
              description="Pinned upstream API versions we ship. The highest releaseDate is the default."
            >
              <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card">
                {row.versions.map((v) => (
                  <li key={v.version} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-mono text-sm font-medium">{v.version}</p>
                      <p className="text-xs text-muted-foreground">
                        released {v.releaseDate}
                      </p>
                    </div>
                    {v.version === row.version && (
                      <Badge variant="secondary">default</Badge>
                    )}
                  </li>
                ))}
              </ul>
            </DetailSection>
          )}

          <div className="flex justify-center pt-2">
            <Button asChild variant="outline">
              <Link to="/integrations">
                Browse all integrations
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCell({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Wrench;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2.5 sm:px-4 sm:py-3">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-base font-semibold leading-none sm:text-lg">
          {value.toLocaleString()}
        </p>
        <p className="mt-0.5 truncate text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      </div>
    </div>
  );
}

function DetailSection({
  title,
  count,
  description,
  search,
  children,
}: {
  title: string;
  count: number;
  description?: string;
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
  };
  children: React.ReactNode;
}) {
  return (
    <FadeIn>
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {title}{" "}
              <span className="ml-1 text-base font-normal text-muted-foreground sm:text-lg">
                {count}
              </span>
            </h2>
            {description && (
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>
        {search && (
          <div className="relative mt-4">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder}
              className="h-10 pl-9 text-sm"
            />
            {search.value && (
              <button
                onClick={() => search.onChange("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <div className="mt-4">{children}</div>
      </div>
    </FadeIn>
  );
}
